import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bank, Wallet, CurrencyDollar, QrCode, Globe, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TYPE_ICONS = {
  bank: Bank, upi: Wallet, paypal: CurrencyDollar, stripe_connect: CurrencyDollar,
  razorpay: CurrencyDollar, venmo: CurrencyDollar, mpesa: Wallet, qr: QrCode, crypto: Globe,
};
const resolveUrl = (v) => (!v ? '' : (/^https?:\/\//i.test(v) ? v : `${API_URL}${v}`));

/** Buyer-facing widget shown on booking confirmation: lists the seller's direct payment methods. */
const SellerPaymentOptions = ({ listingId }) => {
  const [methods, setMethods] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!listingId) return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/listings/${listingId}/seller-payment-methods`);
        if (res.data?.feature_enabled) setMethods(res.data?.methods || []);
      } catch (e) { /* silent */ }
      finally { setLoaded(true); }
    })();
  }, [listingId]);

  if (!loaded || methods.length === 0) return null;

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); }
    catch (e) { toast.error('Copy failed'); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="seller-payment-options">
      <h4 className="text-sm font-bold text-slate-900 mb-1">Or pay seller directly</h4>
      <p className="text-[11px] text-slate-500 mb-3">After paying, the seller will confirm your booking. (These are alternatives to card checkout above.)</p>
      <div className="space-y-2">
        {methods.map(m => {
          const Icon = TYPE_ICONS[m.type] || Wallet;
          return (
            <div key={m.method_id} className="border border-slate-200 rounded-xl p-3 flex items-start gap-3" data-testid={`buyer-payment-option-${m.method_id}`}>
              <Icon size={20} className="text-indigo-500 mt-0.5" weight="fill" />
              <div className="flex-1 min-w-0 text-xs">
                <div className="font-semibold text-slate-900">{m.label}</div>
                {m.type === 'qr' && m.details?.qr_image_url && (
                  <img src={resolveUrl(m.details.qr_image_url)} alt="QR" className="mt-2 w-32 h-32 object-contain rounded-lg border border-slate-200" />
                )}
                {Object.entries(m.details || {}).map(([k, v]) => {
                  if (!v || k === 'qr_image_url') return null;
                  return (
                    <div key={k} className="flex items-center gap-2 mt-1">
                      <span className="text-slate-500 capitalize">{k.replace(/_/g,' ')}:</span>
                      <span className="font-mono text-slate-800 truncate">{v}</span>
                      <button onClick={() => copy(v)} className="text-slate-400 hover:text-slate-700" data-testid={`copy-${m.method_id}-${k}`}>
                        <Copy size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SellerPaymentOptions;
